import { useEffect, useRef } from 'react';

import {
  hideBackButtonSafe,
  mountBackButtonSafe,
  mountMainButtonSafe,
  onBackButtonClickSafe,
  onMainButtonClickSafe,
  setMainButtonParamsSafe,
  showBackButtonSafe,
} from './tg.js';

/**
 * React-хуки владения жизненным циклом нативных Telegram-кнопок (Веха 13.2).
 *
 * Контракт (общий для обоих):
 *  - Эффект mount+listener с пустым deps → навешивает один раз на жизнь экрана;
 *    cleanup ОБЯЗАТЕЛЬНО снимает listener (`off()`) и прячет кнопку. Это закрывает
 *    два риска: (а) при переходах 6-экранной state machine (App.tsx ререндерит
 *    весь экран на setScreen) не копятся двойные подписки; (б) StrictMode в dev
 *    монтирует эффект дважды — cleanup снимает ровно то, что навесил mount.
 *  - **stale-closure:** обработчик держим в `ref`, listener зовёт `handlerRef.current()`.
 *    Так MainButton/BackButton всегда вызывают СВЕЖИЙ handler (например handleSubmit,
 *    замыкающий актуальный текст ответа), а не захваченный на первом рендере. Это
 *    главный риск «кнопка делает не то».
 *  - Эффекты живут ВНУТРИ каждого экрана (экран вызывает хук), не в App/Root —
 *    чтобы слои не гонялись за владение кнопкой.
 */

/**
 * Нативная кнопка «Назад». Монтирует и показывает её, навешивает `onClick`;
 * при размонтировании экрана снимает обработчик и прячет кнопку.
 *
 * @param onClick — действие «назад» (свежее на момент клика).
 * @param visible — показывать ли (по умолчанию true). На корневых экранах
 *   (INBOX/ASK) хук не вызывают вовсе — кнопка остаётся скрытой.
 */
export function useBackButton(onClick: () => void, visible = true): void {
  const handlerRef = useRef(onClick);
  handlerRef.current = onClick;

  useEffect(() => {
    mountBackButtonSafe();
    const off = onBackButtonClickSafe(() => handlerRef.current());
    return () => {
      off?.();
      hideBackButtonSafe();
    };
  }, []);

  useEffect(() => {
    if (visible) showBackButtonSafe();
    else hideBackButtonSafe();
  }, [visible]);
}

/** Параметры главного действия (MainButton). */
export interface MainButtonOptions {
  /** Подпись кнопки. */
  text: string;
  /** Действие при нажатии (свежее на момент клика). */
  onClick: () => void;
  /** Показывать ли кнопку (по умолчанию true). */
  visible?: boolean;
  /** Активна ли (по умолчанию true). Disabled — для пустой формы и т.п. */
  enabled?: boolean;
  /** Показать лоадер на кнопке (по умолчанию false) — на время отправки. */
  loading?: boolean;
}

/**
 * Нативное главное действие (MainButton) — «Отправить вопрос» (Ask),
 * «Ответить» (Answer). Монтирует кнопку, навешивает `onClick`, реактивно
 * прокидывает текст/видимость/enabled/лоадер; при уходе с экрана снимает
 * обработчик и прячет кнопку (`isVisible: false`).
 */
export function useMainButton(opts: MainButtonOptions): void {
  const { text, onClick, visible = true, enabled = true, loading = false } = opts;
  const handlerRef = useRef(onClick);
  handlerRef.current = onClick;

  // mount + listener — один раз на жизнь экрана (StrictMode-safe через cleanup).
  useEffect(() => {
    mountMainButtonSafe();
    const off = onMainButtonClickSafe(() => handlerRef.current());
    return () => {
      off?.();
      setMainButtonParamsSafe({ isVisible: false });
    };
  }, []);

  // Реактивные параметры. Эффект монтирования объявлен выше → на первом рендере
  // выполняется раньше этого, значит к моменту setParams кнопка смонтирована.
  useEffect(() => {
    setMainButtonParamsSafe({
      text,
      isVisible: visible,
      isEnabled: enabled,
      isLoaderVisible: loading,
    });
  }, [text, visible, enabled, loading]);
}
